/**
 * Guest entry — the alternative offered beneath account creation.
 *
 * POSTs straight to the tournament's own entries endpoint, which takes a guest
 * branch when no access token is presented. No account and no session comes out
 * of this: a guest is a name and an address on one entry row.
 *
 * EMAIL IS REQUIRED, and not because anybody wants to write to them. It is the
 * IDENTIFIER — verify-email.js claims every unlinked guest entry matching an
 * address the moment its owner proves they can read that inbox. An entry filed
 * without one can never be handed to the person who earned it. That is why the
 * backend refuses it (entries/index.js:60) and why the note beside the field
 * explains what the address buys rather than apologising for asking.
 *
 * THE CHECKBOX IS A SEPARATE PERMISSION FROM THE ADDRESS, matching
 * contact_consent in the schema: storing it and writing to it are different
 * things. Unchecked is a complete, valid submission — it means they will not be
 * emailed, not that the form is missing something.
 *
 * THE LABEL IS V2-BUILD-SPEC.md:269-270 VERBATIM, not a paraphrase of it. It is
 * long for a checkbox on purpose: waitlist offers are delivered by email, so a
 * guest who declines cannot be offered a spot that opens up and an admin has to
 * confirm on their behalf. The spec requires the copy to make that cost
 * explicit rather than leaving it as a quiet default.
 */

import { useState } from 'react';
import { apiPost } from '../../lib/api.js';

export default function GuestRegisterForm({ venueId, tournamentId, onDone }) {
  const [name, setName]       = useState('');
  const [email, setEmail]     = useState('');
  const [consent, setConsent] = useState(false);
  const [error, setError]     = useState(null);
  const [busy, setBusy]       = useState(false);

  async function onSubmit(event) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    const { data, error: err } = await apiPost(
      `/api/venues/${venueId}/tournaments/${tournamentId}/entries`,
      { guestName: name, guestEmail: email, contactConsent: consent },
    );

    if (err) {
      // "Tournament is full" and "Already registered" both arrive here, and
      // both are things this player needs to read in full.
      setError(err);
      setBusy(false);
      return;
    }

    onDone(data.entry);
  }

  return (
    <form className="hd-stack" onSubmit={onSubmit}>
      <div className="hd-field">
        <label className="hd-label" htmlFor="guest-name">Your name</label>
        <input
          id="guest-name"
          className="hd-input"
          type="text"
          autoComplete="name"
          required
          value={name}
          disabled={busy}
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div className="hd-field">
        <label className="hd-label" htmlFor="guest-email">Email</label>
        <input
          id="guest-email"
          className="hd-input"
          type="email"
          autoComplete="email"
          required
          value={email}
          disabled={busy}
          onChange={(e) => setEmail(e.target.value)}
        />
        <span className="hd-hint">
          You don't need an account to play — this just lets us link your stats
          automatically if you create one later.
        </span>
      </div>

      <label
        className="hd-row"
        style={{ gap: 8, fontSize: 13, alignItems: 'flex-start' }}
      >
        <input
          type="checkbox"
          checked={consent}
          disabled={busy}
          onChange={(e) => setConsent(e.target.checked)}
          style={{ marginTop: 3 }}
        />
        Let us email you about this tournament. If a spot opens up we'll offer
        it to you by email — without this, we can't reach you and you may miss
        your chance.
      </label>

      {error && <div className="hd-badge hd-badge-danger" role="alert">{error}</div>}

      <button className="hd-btn hd-btn-primary" type="submit" disabled={busy}>
        {busy ? 'Registering…' : 'Register as guest'}
      </button>
    </form>
  );
}
